import {ElementRef, Injectable} from '@angular/core';
import {ModalService} from './modal.service';
import {ImageCompressionService} from './image-compression.service';

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {

  // ============================================================================
  // STATE MANAGEMENT - EXACT preservation from LiveDemoComponent
  // ============================================================================

  readonly IMAGE_SIZE_LIMIT = 7 * 1024 * 1024; // 7MB
  readonly VIDEO_SIZE_LIMIT = 100 * 1024 * 1024; // 100MB
  readonly TOTAL_SIZE_LIMIT = 100 * 1024 * 1024; // 100MB
  readonly defaultVideoPlaceholderSvg = `data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 200" width="300" height="200">
    <rect width="100%" height="100%" fill="#f0f0f0"/>
    <g transform="translate(125,87)">
      <circle cx="0" cy="0" r="50" fill="#d0d0d0" opacity="0.8"/>
      <polygon points="-15,-25 25,0 -15,25" fill="#505050"/>
    </g>
    <text x="150" y="165" font-family="Arial" font-size="14" text-anchor="middle" fill="#505050">Video Preview</text>
  </svg>`;
  private selectedFiles: File[] = [];
  private previewUrls: string[] = [];
  private previewItems: { url: string; type: 'image' | 'video'; duration?: number }[] = [];
  // Real measured video lengths (seconds), keyed by the File reference so they
  // survive preview rebuilds (createPreviews clears previewItems each time).
  private videoDurationCache = new Map<File, number>();

  // ============================================================================
  // CONSTANTS - EXACT preservation from LiveDemoComponent
  // ============================================================================
  private isDragOverState = false;
  private totalFileSize = 0;
  private mediaType: 'image' | 'video' | 'any' = 'any';
  private fileInputRef: ElementRef<HTMLInputElement> | null = null;
  private isCompressing = false;
  private showMobileCaptureOptions = false;
  private isMobileDeleteMode = false;

  constructor(
    private modalService: ModalService,
    private imageCompressionService: ImageCompressionService
  ) {
  }

  // ============================================================================
  // FILE INPUT MANAGEMENT - EXACT preservation
  // ============================================================================

  setFileInputRef(fileInput: ElementRef<HTMLInputElement>): void {
    this.fileInputRef = fileInput;
  }

  triggerFileInput(): void {
    if (!this.fileInputRef?.nativeElement) return;

    // Check if this is a mobile device
    if (this.isMobile()) {
      // iOS Safari shows its own native modal anyway, so skip our custom modal
      // to avoid confusing double modals
      if (this.isIOS()) {
        console.log('iOS detected - using native file picker to avoid double modal');
        this.openFilePicker('gallery');
        return;
      }

      // Android and other mobile devices - show our custom modal
      this.showMobileCaptureOptions = true;
      return;
    }

    // Desktop behavior - directly open file picker
    this.openFilePicker('gallery');
  }

  /**
   * Opens the file picker with specific capture mode
   * @param mode - 'camera-photo', 'camera-video', or 'gallery'
   */
  openFilePicker(mode: 'camera-photo' | 'camera-video' | 'gallery'): void {
    if (!this.fileInputRef?.nativeElement) return;

    const input = this.fileInputRef.nativeElement;

    // Configure the input based on the selected mode
    switch (mode) {
      case 'camera-photo':
        input.accept = 'image/*';
        input.setAttribute('capture', 'environment');
        break;
      case 'camera-video':
        input.accept = 'video/*';
        input.setAttribute('capture', 'environment');
        break;
      case 'gallery':
        input.accept = 'image/*,video/*';
        input.removeAttribute('capture');
        break;
    }

    // Reset the file input before triggering the click
    // This ensures the change event will fire even if selecting the same files
    this.resetFileInput();

    // For iOS, use a shorter delay as the native modal handles the timing
    const delay = this.isIOS() ? 10 : 20;

    setTimeout(() => {
      if (this.fileInputRef?.nativeElement) {
        this.fileInputRef.nativeElement.click();
      }
    }, delay);
  }

  /**
   * Handle mobile capture option selection
   */
  selectMobileCaptureOption(option: 'camera-photo' | 'camera-video' | 'gallery'): void {
    this.showMobileCaptureOptions = false;
    this.openFilePicker(option);
  }

  /**
   * Cancel mobile capture options
   */
  cancelMobileCaptureOptions(): void {
    this.showMobileCaptureOptions = false;
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      // Process the files (async but we don't need to await in this context)
      this.handleFiles(Array.from(input.files)).catch(error => {
        console.error('Error handling files:', error);
      });
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Only set isDragging if it's not already true to avoid unnecessary renders
    if (!this.isDragOverState) {
      this.isDragOverState = true;
    }
  }

  // ============================================================================
  // DRAG AND DROP - EXACT preservation
  // ============================================================================

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Check if the mouse has actually left the drop area
    // by checking if the relatedTarget (where the mouse went)
    // is not a child of the drop area
    const dropArea = event.currentTarget as HTMLElement;
    const relatedTarget = event.relatedTarget as Node;

    // Only set isDragging to false if the mouse has truly left the drop area
    if (!dropArea.contains(relatedTarget)) {
      this.isDragOverState = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOverState = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      // Process the files (async but we don't need to await in this context)
      this.handleFiles(Array.from(files)).catch(error => {
        console.error('Error handling files:', error);
      });
    }
  }

  async handleFiles(files: File[]): Promise<void> {
    // Reset any upload error state
    console.log('Handling files:', files);

    // Set compression state to show loading
    this.isCompressing = true;

    try {
      // Determine allowed file types based on existing selections
      const currentMediaType = this.getCurrentMediaType();

      // Helper function to check if a file is a video by extension
      const isVideoByExtension = (file: File): boolean => {
        const videoExtensions = ['.mp4', '.mov', '.avi', '.wmv', '.flv', '.mkv', '.webm'];
        const fileName = file.name.toLowerCase();
        return videoExtensions.some(ext => fileName.endsWith(ext));
      };

      // Extract image and video files
      const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
      const videoFiles = Array.from(files).filter(file =>
        file.type.startsWith('video/') || isVideoByExtension(file)
      );

      // Validate that files match the current media type
      let validFiles: File[] = [];

      if (currentMediaType === 'any') {
        // If no files selected yet, ensure user is only selecting one type
        if (imageFiles.length > 0 && videoFiles.length > 0) {
          this.modalService.showErrorMessage({
            message: 'Please select either images or videos, not both. For the best experience, use only one media type at a time.'
          });
          return;
        }

        // Set the media type based on the first selection
        if (imageFiles.length > 0) {
          this.mediaType = 'image';
          validFiles = imageFiles;
        } else if (videoFiles.length > 0) {
          this.mediaType = 'video';
          validFiles = videoFiles;
        } else {
          this.modalService.showErrorMessage({message: 'Please select valid image or video files.'});
          return;
        }
      } else if (currentMediaType === 'image') {
        // If images already selected, only allow more images
        if (videoFiles.length > 0) {
          this.modalService.showErrorMessage({
            message: 'You already have images selected. Please add only image files or clear your selection first.'
          });
          return;
        }
        validFiles = imageFiles;
      } else if (currentMediaType === 'video') {
        // If videos already selected, only allow more videos
        if (imageFiles.length > 0) {
          this.modalService.showErrorMessage({
            message: 'You already have videos selected. Please add only video files or clear your selection first.'
          });
          return;
        }
        validFiles = videoFiles;
      }

      if (validFiles.length === 0) {
        this.modalService.showErrorMessage({message: `Please select valid ${this.mediaType} files.`});
        return;
      }

      // Separate images and videos for processing
      const imagesToProcess = validFiles.filter(file => file.type.startsWith('image/'));
      const videosToProcess = validFiles.filter(file => !file.type.startsWith('image/'));

      // Compress images if any exist
      let processedFiles: File[] = [];

      if (imagesToProcess.length > 0) {
        console.log(`Compressing ${imagesToProcess.length} images...`);

        // Track original sizes for logging
        const originalSizes = imagesToProcess.map(file => file.size);
        const totalOriginalSize = originalSizes.reduce((sum, size) => sum + size, 0);

        try {
          // Compress images with optimized settings
          const compressedImages = await this.imageCompressionService.compressImages(imagesToProcess, {
            maxWidth: 1600,  // Allows for wider images (landscapes, panoramas)
            maxHeight: 1200, // Allows for taller images (portraits, documents)
            quality: 0.8,
            maxSizeInMB: 2 // Target max size per image
          });

          // Calculate compression statistics
          const compressedSizes = compressedImages.map(file => file.size);
          const totalCompressedSize = compressedSizes.reduce((sum, size) => sum + size, 0);
          const compressionRatio = ((totalOriginalSize - totalCompressedSize) / totalOriginalSize) * 100;

          console.log(`Image compression completed:
            - Original size: ${this.imageCompressionService.formatFileSize(totalOriginalSize)}
            - Compressed size: ${this.imageCompressionService.formatFileSize(totalCompressedSize)}
            - Compression ratio: ${compressionRatio.toFixed(1)}%`);

          processedFiles = [...compressedImages, ...videosToProcess];
        } catch (error) {
          console.error('Error during image compression:', error);
          this.modalService.showErrorMessage({
            message: 'Failed to compress images. Please try again or select different images.'
          });
          return;
        }
      } else {
        // No images to compress, just use the original files
        processedFiles = videosToProcess;
      }

      // Check file size limits on processed files
      const invalidFiles: { name: string, size: number, type: string, reason: string }[] = [];
      let newValidFiles: File[] = [];

      // Calculate current total size
      const currentTotalSize = this.selectedFiles.reduce((total, file) => total + file.size, 0);
      let newTotalSize = currentTotalSize;

      // Check each file against size limits
      processedFiles.forEach(file => {
        // Check individual file size limits first
        if (file.type.startsWith('image/') && file.size > this.IMAGE_SIZE_LIMIT) {
          invalidFiles.push({
            name: file.name,
            size: file.size,
            type: 'image',
            reason: `Image exceeds 7MB limit (${(file.size / (1024 * 1024)).toFixed(2)}MB)`
          });
        } else if (file.type.startsWith('video/') && file.size > this.VIDEO_SIZE_LIMIT) {
          invalidFiles.push({
            name: file.name,
            size: file.size,
            type: 'video',
            reason: `Video exceeds 100MB limit (${(file.size / (1024 * 1024)).toFixed(2)}MB)`
          });
        } else if (newTotalSize + file.size > this.TOTAL_SIZE_LIMIT) {
          // Check total size limit
          invalidFiles.push({
            name: file.name,
            size: file.size,
            type: file.type.startsWith('image/') ? 'image' : 'video',
            reason: `Adding this file would exceed total 100MB limit (current: ${(newTotalSize / (1024 * 1024)).toFixed(1)}MB, file: ${(file.size / (1024 * 1024)).toFixed(1)}MB)`
          });
        } else {
          // File passes all checks
          newValidFiles.push(file);
          newTotalSize += file.size;
        }
      });

      // Show error if any files are invalid
      if (invalidFiles.length > 0) {
        const errorMessages = invalidFiles.map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(2)}MB): ${f.reason}`);

        // Include size limits in the error message when files exceed limits
        this.modalService.showErrorMessage({
          message: `Some files couldn't be added:\n${errorMessages.join('\n')}\n\nLimits: Images 7MB · Videos 100MB · Total 100MB`
        });
        return;
      }

      // Important: Add to existing files instead of replacing them
      const existingFiles = [...this.selectedFiles];
      const newFiles = [...existingFiles, ...newValidFiles];

      // Store the combined files first
      this.selectedFiles = newFiles;
      console.log('Updated selectedFiles:', this.selectedFiles.length, 'files');

      // Update total size from actual files (no need to manually track)
      this.totalFileSize = this.selectedFiles.reduce((total, file) => total + file.size, 0);

      // Create preview URLs for the combined files
      this.createPreviews();
      console.log('After createPreviews - previewItems:', this.previewItems.length);

      // Measure real video lengths up front so track-build can size the song
      // clip to the video (single-video normal mode). Cached by File reference.
      await this.measureVideoDurations();

      // Clear input value to allow selecting the same file again
      // This is critical for mobile browsers to enable selecting more files
      // Use setTimeout to avoid interfering with change detection
      setTimeout(() => {
        this.resetFileInput();
      }, 0);

      // Log the total size
      console.log(`Total upload size: ${(this.totalFileSize / (1024 * 1024)).toFixed(2)}MB / 100MB`);
    } catch (error) {
      console.error('Error handling files:', error);
      this.modalService.showErrorMessage({
        message: 'An error occurred while processing your files. Please try again.'
      });
    } finally {
      // Always reset compression state
      this.isCompressing = false;
    }
  }

  // ============================================================================
  // FILE PROCESSING - EXACT preservation from LiveDemoComponent.handleFiles()
  // ============================================================================

  createPreviews(): void {
    // Clear previous previews
    this.clearPreviews();

    // Create new preview URLs for all files
    this.selectedFiles.forEach(file => {
      const url = URL.createObjectURL(file);
      this.previewUrls.push(url);

      // Determine the file type and add to previewItems. Repopulate the real
      // video duration from the File-keyed cache so rebuilds don't lose it.
      const type = file.type.startsWith('image/') ? 'image' : 'video';
      const duration = type === 'video' ? this.videoDurationCache.get(file) : undefined;
      this.previewItems.push({url, type, duration});
    });

    // Log for debugging
    console.log(`Created ${this.previewUrls.length} previews for ${this.selectedFiles.length} files`);
  }

  /**
   * Measure the real playback length of each selected video and cache it by
   * File reference, then backfill previewItems[i].duration in place. Cached so
   * later createPreviews() rebuilds repopulate duration without re-measuring.
   * Failures/timeouts leave the entry unmeasured (downstream falls back to 30s).
   */
  private async measureVideoDurations(): Promise<void> {
    const tasks = this.selectedFiles.map(file => {
      const isVideo = !file.type.startsWith('image/');
      if (!isVideo || this.videoDurationCache.has(file)) {
        return Promise.resolve();
      }
      return this.measureSingleVideo(file).then(duration => {
        if (duration && duration > 0 && isFinite(duration)) {
          this.videoDurationCache.set(file, duration);
          // selectedFiles may have changed during the async measurement; resolve
          // the current index from the File so we never write to the wrong item.
          const currentIndex = this.selectedFiles.indexOf(file);
          if (currentIndex !== -1 && this.previewItems[currentIndex]) {
            this.previewItems[currentIndex].duration = duration;
          }
        }
      });
    });
    await Promise.all(tasks);
  }

  /**
   * Read a single video's duration (seconds) via a throwaway metadata-only
   * <video> element. Resolves 0 on error/timeout so callers treat it as
   * unmeasured. Streamed/corrupt files can report Infinity; the caller guards
   * against that with isFinite().
   */
  private measureSingleVideo(file: File): Promise<number> {
    return new Promise<number>(resolve => {
      const objectUrl = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';

      const cleanup = () => {
        clearTimeout(timer);
        video.removeAttribute('src');
        video.load();
        URL.revokeObjectURL(objectUrl);
      };

      const timer = setTimeout(() => {
        cleanup();
        resolve(0); // timed out -> treat as unmeasured
      }, 10000);

      video.onloadedmetadata = () => {
        const duration = video.duration;
        cleanup();
        resolve(duration);
      };
      video.onerror = () => {
        cleanup();
        resolve(0);
      };

      video.src = objectUrl;
    });
  }

  // ============================================================================
  // PREVIEW MANAGEMENT - EXACT preservation with memory management
  // ============================================================================

  clearPreviews(): void {
    // Revoke any existing object URLs to prevent memory leaks
    this.previewUrls.forEach(url => URL.revokeObjectURL(url));
    this.previewUrls = [];
    this.previewItems = [];
  }

  removeFile(index: number): void {
    if (index < 0 || index >= this.selectedFiles.length) return;

    // Revoke the URL for this specific file
    URL.revokeObjectURL(this.previewUrls[index]);

    // Drop any cached video duration for this File before the arrays shift.
    this.videoDurationCache.delete(this.selectedFiles[index]);

    // Remove the file and its preview URL
    this.selectedFiles.splice(index, 1);
    this.previewUrls.splice(index, 1);
    this.previewItems.splice(index, 1);

    // Recalculate total size from actual files
    this.totalFileSize = this.selectedFiles.reduce((total, file) => total + file.size, 0);

    // Reset media type to 'any' if all files are removed
    if (this.selectedFiles.length === 0) {
      this.mediaType = 'any';
      console.log('All files removed, resetting media type to: any');
    }

    // Log the updated total size
    console.log(`Total upload size after removal: ${(this.totalFileSize / (1024 * 1024)).toFixed(2)}MB / 100MB`);
  }

  /**
   * Determines the current media type based on selected files
   * EXACT preservation from LiveDemoComponent.getCurrentMediaType()
   */
  getCurrentMediaType(): 'image' | 'video' | 'any' {
    if (this.selectedFiles.length === 0) {
      return 'any';
    }

    // Check if first file is an image or video
    return this.selectedFiles[0].type.startsWith('image/') ? 'image' : 'video';
  }

  // ============================================================================
  // UTILITY METHODS - EXACT preservation
  // ============================================================================

  getSelectedFiles(): File[] {
    return this.selectedFiles;
  }

  // ============================================================================
  // PUBLIC GETTERS - For component integration
  // ============================================================================

  getPreviewItems(): { url: string; type: 'image' | 'video'; duration?: number }[] {
    return this.previewItems;
  }

  getPreviewUrls(): string[] {
    return this.previewUrls;
  }

  isDragging(): boolean {
    return this.isDragOverState;
  }

  getTotalUploadSize(): number {
    // Always calculate from actual files to prevent drift
    this.totalFileSize = this.selectedFiles.reduce((total, file) => total + file.size, 0);
    return this.totalFileSize;
  }

  getMediaType(): 'image' | 'video' | 'any' {
    return this.mediaType;
  }

  isCompressingFiles(): boolean {
    return this.isCompressing;
  }

  getShowMobileCaptureOptions(): boolean {
    return this.showMobileCaptureOptions;
  }

  getIsMobileDeleteMode(): boolean {
    return this.isMobileDeleteMode;
  }

  /**
   * Toggle mobile delete mode
   */
  toggleMobileDeleteMode(): void {
    this.isMobileDeleteMode = !this.isMobileDeleteMode;
  }

  reset(): void {
    this.clearPreviews();
    this.videoDurationCache.clear();
    this.selectedFiles = [];
    this.totalFileSize = 0;
    this.mediaType = 'any';
    this.isDragOverState = false;
    this.showMobileCaptureOptions = false;
    this.isMobileDeleteMode = false;
  }

  cleanup(): void {
    this.clearPreviews();
    this.videoDurationCache.clear();
  }

  // ============================================================================
  // RESET AND CLEANUP
  // ============================================================================

  /**
   * Check if the current device is mobile
   */
  private isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  /**
   * Check if the current device is iOS
   */
  private isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  /**
   * Check if the current device is Android
   */
  private isAndroid(): boolean {
    return /Android/.test(navigator.userAgent);
  }

  /**
   * Resets the file input element completely
   * This is crucial for mobile browsers where simply clearing the value isn't enough
   * Fixed version that preserves Angular event bindings
   */
  private resetFileInput(): void {
    if (!this.fileInputRef?.nativeElement) return;

    const input = this.fileInputRef.nativeElement;

    // Clear the value immediately
    input.value = '';

    // For mobile browsers, we need additional steps to ensure the input is properly reset
    // Use a more Angular-friendly approach that doesn't break event bindings

    // Method 1: Force the input to lose focus (helps with mobile browsers)
    input.blur();

    // Method 2: Reset the form control if it exists
    if (input.form) {
      // Only reset this specific input, not the entire form
      input.value = '';
    }

    // Method 3: Dispatch a custom event to ensure the input is fully reset
    const event = new Event('input', {bubbles: true});
    input.dispatchEvent(event);
  }
}
